import {useEffect, useRef, useState} from "react";

/**
 * Countdown component that counts down from a given number of seconds.
 * @param seconds - The number of seconds to count down from.
 * @param onEnd - Callback function to execute when the countdown ends.
 * @returns A countdown component that displays the remaining time.
 */
const Countdown = ({seconds, onEnd}: {seconds: number; onEnd: () => void}) => {
  // initialize timeLeft with the seconds prop
  const [timeLeft, setTimeLeft] = useState(seconds);

  // Held in a ref rather than listed as a dependency: parents commonly pass an
  // inline arrow, and depending on it would tear down and restart the interval
  // on every parent render, stalling the countdown.
  const onEndRef = useRef(onEnd);
  useEffect(() => {
    onEndRef.current = onEnd;
  }, [onEnd]);

  useEffect(() => {
    // exit early when we reach 0
    if (!timeLeft) {
      onEndRef.current();
      return;
    }

    // save intervalId to clear the interval when the
    // component re-renders
    const intervalId = setInterval(() => {
      setTimeLeft(timeLeft - 1);
    }, 1000);

    // clear interval on re-render to avoid memory leaks
    return () => clearInterval(intervalId);

    // add timeLeft as a dependency to re-rerun the effect
    // when we update it
  }, [timeLeft]);

  return <>{timeLeft}</>;
};

export default Countdown;
